import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { select, Store } from '@ngrx/store';

import { createActionStream, fetchIfNotLoaded } from '@core/ngrx-legacy';
import {
  GrowRolesActions,
  GrowRolesSelectors,
  PositionSkillsActions,
  PositionSkillsSelectors,
  SkillsActions,
  SkillsSelectors,
} from '@core/tellent-skills-data';
import { PositionSkillEntity, SkillEntity } from '@core/tellent-skills-types';

@Injectable({ providedIn: 'root' })
export class TellentSkillsDataFacade {
  private readonly store = inject(Store);

  public readonly assignSkillSuccess$ = createActionStream(
    PositionSkillsActions.assignSkillSuccess,
  );
  public readonly assignSkillMappingSuccess$ = createActionStream(
    PositionSkillsActions.assignSkillMappingSuccess,
  );
  public readonly updateSkillSuccess$ = createActionStream(
    PositionSkillsActions.updateSkillSuccess,
  );
  public readonly unassignSkillSuccess$ = createActionStream(
    PositionSkillsActions.unassignSkillSuccess,
  );
  public readonly unassignSkillMappingSuccess$ = createActionStream(
    PositionSkillsActions.unassignSkillMappingSuccess,
  );

  public readonly growRoles = toSignal(
    this.store.pipe(
      fetchIfNotLoaded(this.store.select(GrowRolesSelectors.meta), () =>
        this.store.dispatch(GrowRolesActions.fetch()),
      ),
      select(GrowRolesSelectors.data),
    ),
    { initialValue: [] },
  );

  public readonly availableSkillEntity: Signal<SkillEntity | null> = this.store.selectSignal(
    SkillsSelectors.data,
  );

  public readonly positionSkillEntity: Signal<PositionSkillEntity | null> = this.store.selectSignal(
    PositionSkillsSelectors.data,
  );

  public readonly isPositionSkillsPending: Signal<boolean> = this.store.selectSignal(
    PositionSkillsSelectors.isPending,
  );

  public fetchAvailableSkills(): void {
    this.store.dispatch(SkillsActions.fetchAvailableSkills());
  }

  public clearPositionSkills(): void {
    this.store.dispatch(PositionSkillsActions.clearPositionSkills());
  }

  public fetchPositionSkills(positionGuid: string): void {
    this.store.dispatch(PositionSkillsActions.fetchPositionSkills({ payload: { positionGuid } }));
  }

  public assignSkill(positionGuid: string, skillId: number, proficiencyId: number): void {
    this.store.dispatch(
      PositionSkillsActions.assignSkill({ payload: { positionGuid, skillId, proficiencyId } }),
    );
  }

  public updateSkill(positionGuid: string, skillId: number, proficiencyId: number): void {
    this.store.dispatch(
      PositionSkillsActions.updateSkill({ payload: { positionGuid, skillId, proficiencyId } }),
    );
  }

  public unassignSkill(positionGuid: string, skillId: number): void {
    this.store.dispatch(
      PositionSkillsActions.unassignSkill({ payload: { positionGuid, skillId } }),
    );
  }

  public unassignSkillMapping(positionGuid: string, skillMappingId: number, levelId: number): void {
    this.store.dispatch(
      PositionSkillsActions.unassignSkillMapping({
        payload: { positionGuid, skillMappingId, levelId },
      }),
    );
  }

  public assignSkillMapping(positionGuid: string, skillMappingId: number, levelId: number): void {
    this.store.dispatch(
      PositionSkillsActions.assignSkillMapping({
        payload: { positionGuid, skillMappingId, levelId },
      }),
    );
  }
}
